<template>
  <div class="employe-list">
    <div v-if="loading" class="unified-table-container">
      <div class="unified-table-skeleton">
        <div v-for="i in 5" :key="i" class="skeleton-row">
          <SkeletonLoader variant="table" />
        </div>
      </div>
    </div>
    <div v-else-if="errorMessage" class="alert alert-danger mb-0">{{ errorMessage }}</div>
    <div v-else-if="!employes || employes.length === 0" class="empty-state-card">
      <div class="empty-state-icon">
        <UserGroupIcon class="w-12 h-12 csl-empty-icon" />
      </div>
      <h4>Aucun employé enregistré</h4>
      <p class="text-muted">Créez un employé avec le formulaire ci-dessus pour commencer.</p>
    </div>
    <div v-else class="unified-table-container">
      <div class="table-controls unified-table-controls">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <input
            v-model.trim="searchQuery"
            type="search"
            placeholder="Rechercher (login, email, nom, typologie, habilitations…)"
            class="form-control form-control-sm search-input unified-search"
            style="max-width: 240px;"
          />
          <label for="page-size-employes" class="mb-0">Afficher</label>
          <select
            id="page-size-employes"
            v-model.number="itemsPerPage"
            class="form-select form-select-sm unified-select"
            style="width: auto; min-width: 70px;"
          >
            <option v-for="n in [10, 20, 30, 50, 100]" :key="n" :value="n">{{ n }}</option>
          </select>
          <span>lignes</span>
        </div>
        <small class="text-muted">{{ startIndex }} - {{ endIndex }} sur {{ filteredEmployes.length }}</small>
      </div>
      <div v-if="isMobile" class="employe-cards">
        <div
          v-for="employe in paginatedEmployes"
          :key="employe.id"
          class="employe-card"
        >
          <div class="employe-card__header">
            <strong class="employe-card__title">{{ employe.login || employe.email || '—' }}</strong>
            <span class="employe-card__role">{{ roleLabel(roleEmploye(employe)) }}</span>
          </div>
          <div class="employe-card__meta">
            <span class="employe-card__label">Email</span>
            <span>{{ employe.email || '—' }}</span>
          </div>
          <div class="employe-card__meta">
            <span class="employe-card__label">ID</span>
            <span>{{ employe.id }}</span>
          </div>
          <div class="employe-card__meta employe-card__meta--block">
            <span class="employe-card__label">Habilitations</span>
            <div class="employe-perm-list">
              <template v-if="!normalizedPermissions(employe).length">
                <span class="text-muted">—</span>
              </template>
              <span
                v-for="p in normalizedPermissions(employe)"
                :key="p"
                class="employe-perm-badge"
              >{{ permissionLabel(p) }}</span>
            </div>
          </div>
          <div class="employe-card__actions">
            <template v-if="canToggleAccesDonnees(employe)">
              <button
                type="button"
                class="btn btn-sm"
                :class="employe.acces_donnees_autorise ? 'btn-success' : 'btn-outline-secondary'"
                :disabled="updatingId === employe.id"
                @click="toggleAccesDonnees(employe)"
              >
                <span v-if="updatingId === employe.id" class="spinner-border spinner-border-sm me-1" role="status"></span>
                {{ employe.acces_donnees_autorise ? 'Autorisé' : 'Non autorisé' }}
              </button>
            </template>
            <button
              type="button"
              class="btn btn-sm btn-outline-danger"
              :disabled="deletingId === employe.id"
              @click="confirmDeleteEmploye(employe)"
            >
              <span v-if="deletingId === employe.id" class="spinner-border spinner-border-sm me-1" role="status"></span>
              Supprimer
            </button>
          </div>
        </div>
        <div v-if="filteredEmployes.length === 0" class="empty-filter text-center text-muted py-4">
          Aucun employé ne correspond à votre recherche.
        </div>
      </div>
      <div v-else class="table-responsive">
        <table class="unified-table">
          <TableHeader :columns="employeColumns" :sticky="true" />
          <tbody>
            <TableRow v-if="filteredEmployes.length === 0" :row-class="'table-row--empty'">
              <td colspan="6" class="text-center text-muted py-4">Aucun employé ne correspond à votre recherche.</td>
            </TableRow>
            <TableRow
              v-for="(employe, idx) in paginatedEmployes"
              :key="employe.id"
              :alt="idx % 2 === 1"
            >
              <td>{{ employe.login || employe.email || '—' }}</td>
              <td>{{ employe.email || '—' }}</td>
              <td>{{ roleLabel(roleEmploye(employe)) }}</td>
              <td class="employe-perms-cell">
                <template v-if="!normalizedPermissions(employe).length">
                  <span class="text-muted">—</span>
                </template>
                <span
                  v-for="p in normalizedPermissions(employe)"
                  :key="p"
                  class="employe-perm-badge"
                >{{ permissionLabel(p) }}</span>
              </td>
              <td class="table-row__cell--actions">
                <div class="d-flex gap-2 flex-wrap">
                  <template v-if="canToggleAccesDonnees(employe)">
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="employe.acces_donnees_autorise ? 'btn-success' : 'btn-outline-secondary'"
                      :disabled="updatingId === employe.id"
                      :title="employe.acces_donnees_autorise ? 'Révoquer l\'accès' : 'Autoriser l\'accès aux données'"
                      @click="toggleAccesDonnees(employe)"
                    >
                      <span v-if="updatingId === employe.id" class="spinner-border spinner-border-sm me-1" role="status"></span>
                      {{ employe.acces_donnees_autorise ? 'Autorisé' : 'Non autorisé' }}
                    </button>
                  </template>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="deletingId === employe.id"
                    @click="confirmDeleteEmploye(employe)"
                    title="Supprimer l'employé"
                  >
                    <span v-if="deletingId === employe.id" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    Supprimer
                  </button>
                </div>
              </td>
            </TableRow>
          </tbody>
        </table>
      </div>
      <div v-if="totalPages > 1" class="pagination-wrap mt-2">
        <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="currentPage === 1" @click="currentPage--">
          Précédent
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="currentPage >= totalPages" @click="currentPage++">
          Suivant
        </button>
      </div>
    </div>
  </div>
</template>
  
<script>
import axios from 'axios';
import { UserGroupIcon } from '@heroicons/vue/24/outline';
import TableHeader from '@/components/dashboard/TableHeader.vue';
import TableRow from '@/components/dashboard/TableRow.vue';
import SkeletonLoader from '@/components/dashboard/SkeletonLoader.vue';
import { isAuthenticatedSession } from '@/utils/sessionAuth';

const TOKEN_KEYS = {
  ACCESS: 'auth_session',
  REFRESH: null /* httpOnly cookie */,
  ROLE: 'user_role',
};

/** Aligné sur gestion/views/helpers.py STAFF_PERMISSIONS (+ libellés UI). */
const STAFF_PERMISSION_LABELS = {
  voir: 'Voir les analyses',
  lecture_seule_global: 'Lecture étendue',
  valider: 'Valider / générer',
  conformite: 'Conformité & KYC',
  gestion_utilisateurs: 'Gestion utilisateurs & stats',
};

export default {
  components: { TableHeader, TableRow, SkeletonLoader, UserGroupIcon },
  data() {
    return {
      employes: [],
      isMobile: false,
      loading: true,
      errorMessage: '',
      updatingId: null,
      deletingId: null,
      itemsPerPage: 10,
      currentPage: 1,
      searchQuery: '',
      employeColumns: [
        { key: 'login', label: 'Login', sortable: false },
        { key: 'email', label: 'Email', sortable: false },
        { key: 'role', label: 'Typologie', sortable: false },
        { key: 'permissions', label: 'Habilitations', sortable: false },
        { key: 'acces_donnees', label: 'Actions', sortable: false, actions: true },
      ],
    };
  },
  computed: {
      filteredEmployes() {
        if (!this.searchQuery) return this.employes;
        const q = this.searchQuery.toLowerCase();
        return this.employes.filter((e) => {
          const login = (e.login || '').toLowerCase();
          const email = (e.email || '').toLowerCase();
          const nom = (e.nom || '').toLowerCase();
          const prenom = (e.prenom || '').toLowerCase();
          const role = String(this.roleEmploye(e) || '').toLowerCase();
          const roleLbl = String(this.roleLabel(this.roleEmploye(e)) || '').toLowerCase();
          const permHay = (this.normalizedPermissions(e) || [])
            .map((p) => `${p} ${this.permissionLabel(p)}`.toLowerCase())
            .join(' ');
          return (
            login.includes(q) ||
            email.includes(q) ||
            nom.includes(q) ||
            prenom.includes(q) ||
            role.includes(q) ||
            roleLbl.includes(q) ||
            permHay.includes(q)
          );
        });
      },
      paginatedEmployes() {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        return this.filteredEmployes.slice(start, start + this.itemsPerPage);
      },
      startIndex() {
        if (!this.filteredEmployes.length) return 0;
        return (this.currentPage - 1) * this.itemsPerPage + 1;
      },
      endIndex() {
        return Math.min(this.currentPage * this.itemsPerPage, this.filteredEmployes.length);
      },
      totalPages() {
        return Math.max(1, Math.ceil(this.filteredEmployes.length / this.itemsPerPage));
      }
    },
    watch: {
      itemsPerPage() { this.currentPage = 1; },
      searchQuery() { this.currentPage = 1; }
    },
    created() {
      this.fetchEmployes();
      // Écouter l'événement de rafraîchissement
        this.$bus.on('refresh-employes', this.fetchEmployes);
    },
    mounted() {
      this.checkMobile();
      window.addEventListener('resize', this.checkMobile);
    },
      beforeUnmount() {
      // Nettoyer l'écouteur d'événements
        this.$bus.off('refresh-employes', this.fetchEmployes);
        window.removeEventListener('resize', this.checkMobile);
    },
    methods: {
      checkMobile() {
        this.isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      },
      async fetchEmployes() {
        this.loading = true;
        this.errorMessage = "";
        
        try {
          if (!isAuthenticatedSession()) {
            this.errorMessage = "Token non trouvé, veuillez vous connecter.";
            this.$router.push('/connexion');
            return;
          }

          const response = await axios.get('/api/employes/', {
            headers: { 'Content-Type': 'application/json' },
          });

          if (response.data) {
            this.employes = response.data;
            // this.employes.forEach(employe => {
            //   if (employe.est_admin) {
            //     this.role = "Administrateur";
            //   } else {
            //     this.role = "Employé";
            //   }
            // });
          } else {
            throw new Error("Aucune donnée reçue du serveur");
          }
        } catch (error) {
          if (error.response?.status === 401) {
            this.errorMessage = "Session expirée, veuillez vous reconnecter.";
          } else {
            this.errorMessage = error.response?.data?.detail || "Une erreur est survenue lors de la récupération des employés.";
          }
        } finally {
          this.loading = false;
        }
      },
      roleEmploye(employe) {
        return employe.role_employe || employe.role || '—';
      },
      roleLabel(role) {
        const labels = {
          conseiller: 'Conseiller',
          it: 'IT / technique',
          admin: 'Administrateur',
          employe: 'Employé',
          saisie: 'Saisie',
          validation: 'Validation et modification',
        };
        return labels[role] || role || '—';
      },
      normalizedPermissions(employe) {
        const raw = employe && employe.permissions;
        if (!Array.isArray(raw)) return [];
        return [...new Set(raw.map((p) => String(p || '').trim()).filter(Boolean))].sort();
      },
      permissionLabel(key) {
        const k = String(key || '').trim();
        return STAFF_PERMISSION_LABELS[k] || k;
      },
      canToggleAccesDonnees(employe) {
        const r = this.roleEmploye(employe);
        return r === 'saisie' || r === 'conseiller';
      },
      async toggleAccesDonnees(employe) {
        if (!this.canToggleAccesDonnees(employe)) return;
        const newVal = !employe.acces_donnees_autorise;
        this.updatingId = employe.id;
        try {
          await axios.patch(
            `/api/employes/${employe.id}/acces-donnees/`,
            { acces_donnees_autorise: newVal },
            { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
          );
          employe.acces_donnees_autorise = newVal;
        } catch (err) {
          this.errorMessage = err.response?.data?.detail || 'Erreur lors de la mise à jour.';
        } finally {
          this.updatingId = null;
        }
      },
      async confirmDeleteEmploye(employe) {
        if (!employe?.id) return;
        const label = employe.login || employe.email || `Employé #${employe.id}`;
        const ok = window.confirm(`Supprimer définitivement l'employé ${label} ? Cette action est irréversible.`);
        if (!ok) return;
        await this.deleteEmploye(employe.id);
      },
      async deleteEmploye(employeId) {
        if (!isAuthenticatedSession()) {
          this.errorMessage = "Token non trouvé, veuillez vous connecter.";
          this.$router.push('/connexion');
          return;
        }
        this.deletingId = employeId;
        try {
          await axios.delete(`/api/admin/employes/${employeId}/delete/`, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          });
          this.employes = this.employes.filter((e) => e.id !== employeId);
          if (this.currentPage > this.totalPages) this.currentPage = this.totalPages;
        } catch (err) {
          this.errorMessage = err.response?.data?.detail || "Erreur lors de la suppression de l'employé.";
        } finally {
          this.deletingId = null;
        }
      },
    },
  };
  </script>
  
  <style scoped>
  .employe-list {
    padding: 0 0.5rem;
  }

  .unified-table-container {
    background: var(--bank-white);
    border-radius: 16px;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    box-shadow: 0 1px 3px rgba(10, 26, 47, 0.05), 0 12px 32px rgba(10, 26, 47, 0.06);
    border: 1px solid color-mix(in srgb, var(--bank-navy) 8%, var(--bank-gray-200));
    padding: 20px 24px;
  }

  .unified-table-controls {
    margin-bottom: 16px;
  }

  .unified-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
  }

  .unified-table-skeleton {
    padding: 16px 0;
  }

  .unified-table-skeleton .skeleton-row {
    margin-bottom: 8px;
  }

  .unified-table-skeleton .skeleton-row:last-child {
    margin-bottom: 0;
  }

  /* En-têtes des tableaux admin : fond navy, titres en blanc */
  .employe-list :deep(thead.table-header) {
    background: var(--brand-navy);
    border-bottom: none;
  }
  .employe-list :deep(.table-header__cell) {
    color: #ffffff;
  }
  .employe-list :deep(.table-header__cell--sortable:hover) {
    color: rgba(255, 255, 255, 0.95);
  }
  .employe-list :deep(.table-header__sort-icon) {
    color: rgba(255, 255, 255, 0.9);
  }

  .employe-list .text-muted { color: var(--bank-gray-500); }
  .table-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    flex-wrap: wrap;
    gap: 8px;
  }
  .pagination-wrap {
    display: flex;
    gap: 6px;
  }
  .empty-state-card {
    text-align: center;
    padding: 2.5rem 1.5rem;
  background: var(--bank-gray-50);
  border-radius: var(--radius-lg);
  border: 1px dashed var(--bank-gray-200);
  }
  .empty-state-icon {
    font-size: 2.5rem;
  color: var(--bank-gray-400);
    margin-bottom: 0.75rem;
  }
  .empty-state-card h4 {
    font-size: 1.1rem;
    font-weight: 600;
  color: var(--bank-gray-800);
    margin: 0 0 0.5rem 0;
  }
  .search-input:focus {
  border-color: var(--bank-primary);
  box-shadow: 0 0 0 0.2rem rgba(139, 92, 42, 0.18);
  }
  .empty-filter {
    font-size: 0.9rem;
  }

  .employe-cards {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .employe-card {
    border: 1px solid rgba(226, 232, 240, 0.9);
    border-radius: 12px;
    padding: 1rem;
    background: #fff;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  .employe-card__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .employe-card__title {
    color: var(--bank-navy);
  }

  .employe-card__role {
    display: inline-flex;
    align-items: center;
    padding: 0.3rem 0.6rem;
    border-radius: 999px;
    background: color-mix(in srgb, var(--bank-gold) 18%, var(--bank-white));
    color: var(--bank-navy);
    border: 1px solid color-mix(in srgb, var(--bank-gold-dark) 35%, var(--bank-gray-200));
    font-size: 0.8rem;
    font-weight: 600;
    white-space: nowrap;
  }

  .csl-empty-icon {
    color: var(--bank-gray-400);
  }

  .employe-card__meta {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
    color: #475569;
    font-size: 0.92rem;
  }

  .employe-card__label {
    font-weight: 600;
    color: #334155;
  }

  .employe-card__actions {
    margin-top: 0.75rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .employe-card__meta--block {
    flex-direction: column;
    align-items: flex-start;
  }

  .employe-perm-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-top: 0.15rem;
  }

  .employe-perm-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.15rem 0.45rem;
    border-radius: 999px;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: var(--bank-navy);
    background: color-mix(in srgb, var(--bank-gold) 14%, var(--bank-white));
    border: 1px solid color-mix(in srgb, var(--bank-gold-dark) 28%, var(--bank-gray-200));
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .employe-perms-cell {
    max-width: 280px;
  }

  .employe-perms-cell .employe-perm-badge {
    margin-right: 0.25rem;
    margin-bottom: 0.25rem;
  }

  @media (max-width: 768px) {
    .employe-list {
      padding: 0;
    }

    .unified-table-container {
      padding: 14px 16px;
      border-radius: 12px;
    }

    .table-controls {
      flex-direction: column;
      align-items: flex-start;
    }

    .table-controls .d-flex {
      width: 100%;
      flex-direction: column;
      align-items: stretch !important;
      gap: 0.75rem !important;
    }

    .search-input {
      max-width: none !important;
      width: 100%;
    }

    .employe-card__header,
    .employe-card__meta {
      flex-direction: column;
      align-items: flex-start;
    }

    .employe-card__actions .btn {
      width: 100%;
      min-height: 42px;
      justify-content: center;
    }

    .pagination-wrap {
      flex-wrap: wrap;
      gap: 8px;
    }

    .pagination-wrap .btn {
      flex: 1 1 auto;
    }
  }
  </style>
  
